import React from 'react';

export type ToastVariant = 'success' | 'error' | 'warning' | 'info';

type ToastState = {
  showToast: boolean;
  toastMessage: string;
  toastVariant: ToastVariant;
};

type ShowOptions = {
  message: string;
  variant?: ToastVariant;
  duration?: number;
  onHide?: () => void;
};

export const useToast = () => {
  const [state, setState] = React.useState<ToastState>({
    showToast: false,
    toastMessage: '',
    toastVariant: 'success',
  });

  const timerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const onHideRef = React.useRef<(() => void) | undefined>(undefined);

  const hide = React.useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    onHideRef.current?.();
    onHideRef.current = undefined;
    setState({ showToast: false, toastMessage: '', toastVariant: 'success' });
  }, []);

  const show = React.useCallback((opts: ShowOptions) => {
    const { message, variant = 'success', duration = 3000, onHide } = opts;

    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    onHideRef.current = onHide;
    setState({ showToast: true, toastMessage: message, toastVariant: variant });

    if (duration > 0) {
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        const callback = onHideRef.current;
        onHideRef.current = undefined;
        setState({ showToast: false, toastMessage: '', toastVariant: 'success' });
        callback?.();
      }, duration);
    }
  }, []);

  React.useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      onHideRef.current = undefined;
    };
  }, []);

  return {
    showToast: state.showToast,
    toastMessage: state.toastMessage,
    toastVariant: state.toastVariant,
    show,
    hide,
  } as const;
};

export default useToast;
